import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import isEqual from 'lodash-es/isEqual';
import momentPropTypes from 'react-moment-proptypes';
import Toggle from 'react-toggle';

import intlShape from '@/shared/utils/intlShape';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import api from '@/shared/api';

import RelationSearch from '@/shared/components/input/RelationSearch';
import AuthService from '@/shared/services/AuthService';
import UserSearch from '@/shared/components/input/UserSearch';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import guid from '@/shared/utils/guid';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';
import ToastrService from '@/shared/services/ToastrService';
import userStorage from '@/shared/services/userStorage';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import PeriodButtonGroup, { getPeriodStartEndDates } from '@/shared/components/PeriodButtonGroup';
import DynamicTaskFilter, {
  getDynamicFiltersFromDefaultFilters,
  handleDynamicFiltersChanged,
  initDynamicFilters,
  resetDynamicFilters,
} from '../DynamicTaskFilter';
import messages from '../taskMessages';
import config from '../../../config';
import HasFeature from '@/shared/components/security/HasFeature';
import { getTaskPeriodicityLineDescription } from '@/shared/services/ObjectDisplayService';

const periods = [
  'month-1',
  'month',
  'nextMonth',
  'quarter-1',
  'quarter',
  'nextQuarter',
  'year-1',
  'year',
  'year+1',
  'all',
];

export const defaultFilters = {
  taskId: null,
  includeParameters: false,
  relationId: null,
  invoiceStreamId: null,
  relationName: null,
  relationTypes: [],
  deadlineFrom: moment.utc().startOf('month'),
  deadlineUntil: moment.utc().endOf('month'),
  period: 'month',
  executionDateFrom: null,
  executionDateUntil: null,
  periodicityId: null,
  periodicityLineId: null,
  ownCompanies: [],
  legalEntities: [],
  relationTags: [],
  withoutRelationTags: [],
  userId: null,
  relationUserId: null,
  assistant: null, // UserId
  responsible: null, // UserId
  associate: null, // UserId
  assistantBackup: null, // UserId
  billingUser: null, // UserId
  seniorUser: null, // UserId
  assignmentTemplateId: null,
  endFiscalYears: [],
  accountingSoftware: [],
  receiveInvoiceSoftware: [],
  otherSoftware: [],
  sendInvoiceSoftware: [],
  teams: [],
  year: null,
  fiscalEndYear: null,
  vatPeriodicity: [],
  createdBy: null,
  legalForms: [], // Array
  financialStatementScheme: [],
  socialSecurityFund: [],
  socialSecretariat: [],
  withTags: [],
  withoutTags: [],
  relationGroups: [],
  filterBehaviours: {
    withTags: 1,
    relationTags: 1,
  },
  mixedTaxPayer: null,
  mixedTaxPayerVatDeductionMethod: [],
  selfBooker: null,
  relationHandlingLevels: [],
  inactiveRelation: null,
  capitalGainsTax: [],
};
const getIgnoredFilters = filters => {
  const result = ['username', 'relationUsername'];
  if (filters.period) {
    // Ignore deadline filters if there is a period
    result.push('deadlineUntil', 'deadlineFrom');
  }
  return result;
};

const allDynamicFilters = [
  ...getDynamicFiltersFromDefaultFilters(defaultFilters),
  { id: 'periodicityLineId', label: messages.TaskPeriodicityLine },
];

const defaultDynamicFiltersStorageKey = 'TaskOverviewDynamicFilters';

class TaskOverviewFilters extends PureComponent {
  state = {
    periodicities: null,
    dynamicFilters: [],
    taskLabels: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      taskId: PropTypes.string,
      relationId: PropTypes.string,
      invoiceStreamId: PropTypes.string,
      relationName: PropTypes.string,
      relationTypes: PropTypes.arrayOf(PropTypes.number),
      linkTypes: PropTypes.arrayOf(PropTypes.number),
      deadlineUntil: momentPropTypes.momentObj,
      deadlineFrom: momentPropTypes.momentObj,
      period: PropTypes.oneOf(periods),
      executionDateFrom: momentPropTypes.momentObj,
      executionDateUntil: momentPropTypes.momentObj,
      periodicityId: PropTypes.string,
      periodicityLineId: PropTypes.string,
      includeParameters: PropTypes.bool,
      relationTags: PropTypes.arrayOf(PropTypes.string),
      withoutRelationTags: PropTypes.arrayOf(PropTypes.string),
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      userId: PropTypes.string,
      username: PropTypes.string,
      relationUserId: PropTypes.string,
      responsible: PropTypes.string,
      assistant: PropTypes.string,
      associate: PropTypes.string,
      assistantBackup: PropTypes.string,
      billingUser: PropTypes.string,
      seniorUser: PropTypes.string,
      year: PropTypes.number,
      fiscalEndYear: PropTypes.number,
      assignmentTemplateId: PropTypes.string,
      accountingSoftware: PropTypes.arrayOf(PropTypes.number),
      receiveInvoiceSoftware: PropTypes.arrayOf(PropTypes.number),
      otherSoftware: PropTypes.arrayOf(PropTypes.number),
      sendInvoiceSoftware: PropTypes.arrayOf(PropTypes.number),
      createdBy: PropTypes.string,
      legalForms: PropTypes.arrayOf(PropTypes.number),
      withTags: PropTypes.arrayOf(PropTypes.string),
      withoutTags: PropTypes.arrayOf(PropTypes.string),
      relationGroups: PropTypes.arrayOf(PropTypes.string),
      userIsNextAssignedUser: PropTypes.bool,
      inactiveRelation: PropTypes.number,
      capitalGainsTax: PropTypes.arrayOf(PropTypes.number),
    }),
    displayMode: PropTypes.oneOf(['status', 'dateDone', 'deadline', 'executionDate']),
    displayModeChanged: PropTypes.func,
    templates: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
    ).isRequired,
    columnFilters: PropTypes.shape({}),
    showIncludeParameters: PropTypes.bool,
    showDisplayModeToggles: PropTypes.bool,
    showNextInLineToggle: PropTypes.bool,
    renderTrailingToggles: PropTypes.func,
    screen: PropTypes.string,
    dynamicFiltersStorageKey: PropTypes.string,
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    filters: defaultFilters,
    columnFilters: {},
    showIncludeParameters: true,
    showDisplayModeToggles: true,
    showNextInLineToggle: true,
    renderTrailingToggles: null,
    displayMode: 'status',
    displayModeChanged: () => {},
    screen: 'TaskOverview',
    dynamicFiltersStorageKey: defaultDynamicFiltersStorageKey,
  };

  componentDidMount() {
    const { filters, intl, dynamicFiltersStorageKey } = this.props;

    this.setState({
      dynamicFilters: initDynamicFilters(
        intl,
        allDynamicFilters,
        userStorage.getItem(dynamicFiltersStorageKey) || [],
        filters,
        defaultFilters,
      ),
    });

    this.retrievePeriodicities(filters.taskId);

    if (filters && filters.period) {
      this.setPeriod(filters.period);
    }
    api.base.get(`${config.appUrls.api}/api/Resource/Tags`).then(({ data }) => {
      this.setState({
        relationLabels: data
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase(), color: x.color }))
          .sort(dynamicSort('sort')),
      });
    });
    api.base.get(`${config.appUrls.tasks}/api/Tags`).then(({ data }) => {
      this.setState({
        taskLabels: data
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
          .sort(dynamicSort('sort')),
      });
    });
  }

  componentDidUpdate(prevProps) {
    const { filters: newFilters } = this.props;
    const { filters: oldFilters } = prevProps;

    if (oldFilters.taskId !== newFilters.taskId) {
      this.retrievePeriodicities(newFilters.taskId);
    }
  }

  retrievePeriodicities = templateId => {
    const { intl } = this.props;
    if (!templateId) {
      this.setState({ periodicities: null });
      return;
    }
    return api.base
      .get(`${config.appUrls.tasks}/api/TaskPeriodicity/${templateId}`)
      .then(({ data }) => {
        const translatedPeriodicities = data
          .map(t => ({
            ...t,
            label: intl.formatMessage(listItemMsg('taskperiodicity', t.id, t.name)), // Map translation to label property
          }))
          .sort(dynamicSort('label'));

        this.setState({
          periodicities: translatedPeriodicities,
        });
      });
  };

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };
    if (filters !== newFilters) onFilterChange(newFilters);
  };
  dynamicFilterChanged = (item, selected) => {
    this.setState(({ dynamicFilters }) => {
      const { filters, dynamicFiltersStorageKey } = this.props;
      const newFilters = handleDynamicFiltersChanged(
        dynamicFilters,
        item,
        selected,
        filters,
        defaultFilters,
      );
      userStorage.setItem(
        dynamicFiltersStorageKey,
        newFilters.filter(x => x.selected).map(x => x.id),
      );
      if (
        !selected &&
        item.id !== 'selectAll' &&
        !isEqual(filters[item.id], defaultFilters[item.id])
      ) {
        // Reset filter when it's removed
        this.filterChanged({ [item.id]: defaultFilters[item.id] });
      }
      return { dynamicFilters: newFilters };
    });
  };

  templateChanged = event => {
    const taskId = event.target.value;
    const { filters: { periodicityId } = {} } = this.props;
    this.filterChanged({
      taskId,
      periodicityId: periodicityId === guid.empty ? periodicityId : null,
      periodicityLineId: null,
      fiscalEndYear: null,
    });

    this.retrievePeriodicities(taskId);
  };

  periodicityChanged = event => {
    const periodicityId = event.target.value || null;
    this.filterChanged({ periodicityId, periodicityLineId: null, fiscalEndYear: null });
  };

  deadlineFromChanged = value => {
    this.filterChanged({ deadlineFrom: value });
  };
  deadlineUntilChanged = value => {
    this.filterChanged({ deadlineUntil: value });
  };

  deadlineChanged = (startDate, endDate) => {
    this.filterChanged({ deadlineFrom: startDate, deadlineUntil: endDate, period: null });
  };
  periodChanged = ({ dateFrom, dateUntil, period }) => {
    this.filterChanged({ deadlineFrom: dateFrom, deadlineUntil: dateUntil, period });
  };

  relationChanged = item => {
    if (!item || !item.invoiceStreamId) {
      this.filterChanged({ invoiceStreamId: null, relationId: null, relationName: null });
    } else {
      this.filterChanged({
        invoiceStreamId: null,
        relationId: item.key,
        relationName: item.label,
      });
    }
  };

  userChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ userId: null, username: null });
    } else {
      this.filterChanged({
        userId: item.id,
        username: item.name,
      });
    }
  };

  includeParametersChanged = checked => {
    this.filterChanged({
      includeParameters: checked,
    });
  };

  setPeriod = value => {
    const [deadlineFrom, deadlineUntil, period] = getPeriodStartEndDates(value);
    this.filterChanged({ deadlineFrom, deadlineUntil, period });
  };
  savedFilterChanged = ({ displayMode, ...filters }) => {
    const { onFilterChange, displayModeChanged, intl } = this.props;
    const newFilters = { ...defaultFilters, ...filters };
    if (typeof newFilters.deadlineFrom === 'string')
      newFilters.deadlineFrom = moment.utc(filters.deadlineFrom);
    if (typeof newFilters.deadlineUntil === 'string')
      newFilters.deadlineUntil = moment.utc(filters.deadlineUntil);
    if (typeof newFilters.executionDateFrom === 'string')
      newFilters.executionDateFrom = moment.utc(filters.executionDateFrom);
    if (typeof newFilters.executionDateUntil === 'string')
      newFilters.executionDateUntil = moment.utc(filters.executionDateUntil);

    if (newFilters.period) {
      const [deadlineFrom, deadlineUntil] = getPeriodStartEndDates(newFilters.period);
      newFilters.deadlineFrom = deadlineFrom;
      newFilters.deadlineUntil = deadlineUntil;
    }
    const { templates } = this.props;
    if (newFilters.taskId && templates && !templates.some(x => x.id === newFilters.taskId)) {
      // Task template not found, reset to null
      newFilters.taskId = null;
      ToastrService.warning(intl.formatMessage(messages.FilterTaskTemplateNotFound));
    }

    onFilterChange(newFilters);
    if (displayMode) {
      displayModeChanged(displayMode);
    }

    this.setState(({ dynamicFilters }) => ({
      dynamicFilters: resetDynamicFilters(dynamicFilters, newFilters, defaultFilters),
    }));

    const { taskId } = newFilters;
    this.retrievePeriodicities(taskId);
  };
  renderCustomFilter = ({ type, filters, filterChanged, intl, columnClassName }) => {
    const { periodicities } = this.state;
    switch (type) {
      case 'periodicityLineId': {
        const periodicity =
          periodicities && filters.periodicityId
            ? periodicities.find(x => x.id === filters.periodicityId)
            : null;

        return (
          <div className={columnClassName}>
            <div className="form-group">
              <select
                className="form-control"
                value={filters.periodicityLineId || ''}
                onChange={e => filterChanged({ periodicityLineId: e.target.value || null })}
                disabled={!periodicity || !periodicity.lines || periodicity.lines.length === 0}
              >
                {!periodicity ? (
                  <option value="">{intl.formatMessage(messages.SelectPeriodicityFirst)}</option>
                ) : !periodicity.lines || periodicity.lines.length === 0 ? (
                  <option value="">{intl.formatMessage(messages.NoPeriodicityLines)}</option>
                ) : (
                  <>
                    <option value="">
                      {intl.formatMessage(
                        filters.periodicityLineId
                          ? messages.ClearFilter
                          : messages.TaskPeriodicityLine,
                      )}
                    </option>
                    {periodicity.lines.map(l => (
                      <option key={l.id} value={l.id}>
                        {getTaskPeriodicityLineDescription(l, intl)}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  render() {
    const { periodicities, relationLabels, dynamicFilters, taskLabels } = this.state;
    const {
      filters,
      intl,
      displayMode,
      displayModeChanged,
      countSelectedTasks,
      columnFilters,
      showIncludeParameters,
      showDisplayModeToggles,
      showNextInLineToggle,
      renderTrailingToggles,
      screen,
      templates,
    } = this.props;

    let initRelation = null;

    if (filters.relationId && filters.relationName) {
      initRelation = {
        key: filters.relationId,
        label: filters.relationName,
        invoiceStreamId: filters.invoiceStreamId,
      };
    }
    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);

    const defaultFiltersForSaving = {
      ...defaultFilters,
      // Add current taskId so the reset doesn't change the task
      taskId: filters.taskId || null,
      columnFilters: {},
      displayMode: 'status',
    };
    // When a user visits this screen for the first time, they still need to select a task template.
    // To prevent saving filters without taskid, we use the default filter as current filter, so the button doesn't detect a difference.
    // This is better than hiding the button because now they can still select a saved filter.
    const currentFiltersForSaving = filters.taskId
      ? {
          ...filters,
          columnFilters,
          displayMode,
        }
      : defaultFiltersForSaving;

    return (
      <div className="info-block container-fluid">
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              {templates && (
                <select
                  onChange={this.templateChanged}
                  className="form-control"
                  value={filters.taskId || ''}
                >
                  {!filters.taskId && <option>{intl.formatMessage(messages.SelectTask)}</option>}
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              {!filters.taskId && !periodicities && (
                <select className="form-control" value="" disabled>
                  <option value="" disabled>
                    {intl.formatMessage(messages.ChooseTaskFirst)}
                  </option>
                </select>
              )}
              {periodicities && (
                <select
                  onChange={this.periodicityChanged}
                  className="form-control"
                  value={filters.periodicityId || ''}
                >
                  <option value="">
                    {filters.periodicityId
                      ? intl.formatMessage(messages.ClearFilter)
                      : intl.formatMessage(messages.FilterPeriodicity)}
                  </option>

                  <option value={guid.empty}>{intl.formatMessage(messages.NoPeriodicity)}</option>
                  {periodicities && !!periodicities.length && (
                    <option
                      style={{ fontSize: '1pt' }}
                      className="grey"
                      disabled="disabled"
                      value="&nbsp;"
                    >
                      &nbsp;
                    </option>
                  )}
                  {periodicities.map(periodicity => (
                    <option key={periodicity.id} value={periodicity.id}>
                      {periodicity.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <RelationSearch onChange={this.relationChanged} initialSelectedItem={initRelation} />
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <UserSearch
                placeholder={intl.formatMessage(messages.TasksAssignedTo)}
                onChange={this.userChanged}
                value={filters.userId}
                withCurrentUser
                withNotAssignedToMe
                withUnassigned
                unassignedId={guid.empty}
              />
            </div>
          </div>
        </div>
        {visibleFilters && visibleFilters.length > 0 ? (
          <div className="row">
            {visibleFilters.map(id => (
              <DynamicTaskFilter
                key={id}
                type={id}
                intl={intl}
                labels={relationLabels}
                taskLabels={taskLabels}
                filters={filters}
                filterChanged={this.filterChanged}
                columnClassName="col-lg-3 col-md-6"
                renderCustomFilter={this.renderCustomFilter}
              />
            ))}
          </div>
        ) : null}
        <div className="row">
          <div className="col-lg-9 col-lg-push-3">
            <div className="form-group">
              <PeriodButtonGroup
                dateFrom={filters.deadlineFrom}
                dateUntil={filters.deadlineUntil}
                period={filters.period}
                visiblePeriods={periods}
                onChange={this.periodChanged}
              />
            </div>
          </div>
          <div className="col-lg-3 col-lg-pull-9">
            <div className="row">
              <div className="col-lg-12 col-md-6">
                <div className="form-group">
                  <div className="cx-filter-date-range-with-icon">
                    <i
                      className="fa fa-calendar cx-filter-date-range-with-icon__icon"
                      title={intl.formatMessage(messages.Deadline)}
                      aria-hidden="true"
                    />
                    <DelayedDateRangePicker
                      startDate={filters.deadlineFrom}
                      startDateId="TaskOverviewFilterDeadlineFrom"
                      endDate={filters.deadlineUntil}
                      endDateId="TaskOverviewFilterDeadlineUntil"
                      onDatesChange={({ startDate, endDate }) =>
                        this.deadlineChanged(startDate, endDate)
                      }
                      startDatePlaceholderText={intl.formatMessage(
                        messages.DeadlineFromPlaceholder,
                      )}
                      endDatePlaceholderText={intl.formatMessage(messages.DeadlineUntilPlaceholder)}
                      block
                    />
                  </div>
                </div>
              </div>
              {showNextInLineToggle && (
                <div className="col-lg-12 col-md-6">
                  <div className="form-group">
                    <label
                      className="react-toggle-wrapper"
                      title={intl.formatMessage(
                        messages.ShowTasksWhereSelectedUserIsNextInLineTooltip,
                      )}
                    >
                      <Toggle
                        checked={filters.userIsNextAssignedUser}
                        onChange={e =>
                          this.filterChanged({
                            userIsNextAssignedUser: !filters.userIsNextAssignedUser,
                            // when enabling this filter and no userId is filtered, set the userId to the current user
                            userId:
                              !filters.userIsNextAssignedUser === true && !filters.userId
                                ? AuthService.getUserId()
                                : filters.userId,
                          })
                        }
                      />
                      <FormattedMessage
                        className="label-text"
                        {...messages.ShowTasksWhereSelectedUserIsNextInLine}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <SavedFiltersButton
              screen={screen}
              currentFilters={currentFiltersForSaving}
              defaultFilters={defaultFiltersForSaving}
              onFiltersChanged={this.savedFilterChanged}
              ignoreFilters={getIgnoredFilters(filters)}
              dynamicFilters={dynamicFilters}
              onDynamicFiltersChanged={this.dynamicFilterChanged}
            />
          </div>
          <div className="col-md-6">
            <div className="form-group">
              {renderTrailingToggles
                ? renderTrailingToggles()
                : showDisplayModeToggles && (
                    <>
                      <label className="react-toggle-wrapper margin10R">
                        <Toggle
                          checked={displayMode === 'dateDone'}
                          onChange={e =>
                            displayModeChanged(e.target.checked ? 'dateDone' : 'status')
                          }
                        />
                        <FormattedMessage className="label-text" {...messages.ShowDateDone} />
                      </label>
                      <label className="react-toggle-wrapper margin10R">
                        <Toggle
                          checked={displayMode === 'deadline'}
                          onChange={e =>
                            displayModeChanged(e.target.checked ? 'deadline' : 'status')
                          }
                        />
                        <FormattedMessage className="label-text" {...messages.ShowDeadline} />
                      </label>
                      <HasFeature feature="capacityPlanning">
                        <label className="react-toggle-wrapper margin10R">
                          <Toggle
                            checked={displayMode === 'executionDate'}
                            onChange={e =>
                              displayModeChanged(e.target.checked ? 'executionDate' : 'status')
                            }
                          />
                          <FormattedMessage
                            className="label-text"
                            {...messages.ShowExecutionDate}
                          />
                        </label>
                      </HasFeature>
                      <label className="react-toggle-wrapper margin10R">
                        <Toggle
                          checked={displayMode === 'assignedTo'}
                          onChange={e =>
                            displayModeChanged(e.target.checked ? 'assignedTo' : 'status')
                          }
                        />
                        <FormattedMessage className="label-text" {...messages.ShowAssignedTo} />
                      </label>
                    </>
                  )}
              {!renderTrailingToggles && showIncludeParameters && (
                <label className="react-toggle-wrapper">
                  <Toggle
                    checked={filters.includeParameters}
                    onChange={e => this.includeParametersChanged(e.target.checked)}
                  />
                  <FormattedMessage className="label-text" {...messages.ShowParameters} />
                </label>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12 batch-count">
            <p>{countSelectedTasks}</p>
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(TaskOverviewFilters);
